<template>
  <div
    :id="props.item.id"
    ref="dropArea"
    class="tree-node"
    :class="{
      'active': active,
      'error': error,
      'calculating': calculating,
      'allowed-drop': isValidDropArea,
      'disallowed-drop':
        !isValidDropArea && dragItem && !props.item.id.startsWith('copy-')
    }"
    :data-cy="`tree-node-${props.item.id}`">
    <v-icon
      :icon="chevron"
      @click="() => handleOpen(props.item.id)"
      :data-cy="`tree-node-open-${props.item.id}`" />

    <div
      ref="treeNodeDrag"
      :data-cy="`tree-node-drag-handle-${props.item.id}`"
      draggable="true">
      <v-icon
        icon="mdi-drag-vertical"
        style="cursor: grab" />
    </div>

    <div
      class="tree-node-content"
      @click="handleActive(props.item)"
      :data-cy="`tree-node-content-${props.item.id}`">
      <icon-loader :name="props.item.elementType" />
      <span>{{ props.item.title }} </span>
      <v-avatar
        v-if="mainStore.activeElements[props.item.id]"
        size="x-small"
        color="primary">
        {{ mainStore.getInitials(mainStore.activeElements[props.item.id]) }}
      </v-avatar>
    </div>
  </div>
  <div
    v-if="open"
    style="margin-left: 20px">
    <tree-node
      v-if="dropInArea && mainStore.dragItem"
      :key="mainStore.dragItem.id + 'copy'"
      :item="mainStore.dragItem.item"
      :level="props.level + 1" />
    <tree-node
      v-for="child in props.item.children"
      :key="child.id"
      :item="child"
      :level="props.level + 1" />
  </div>

  <!-- floating element -->
  <!-- <div
    v-if="isDragging"
    :id="props.item.id"
    class="tree-node"
    style="position: fixed; opacity: 0.5"
    :style="dragStyle"
  >
    <div />
    <v-icon
      icon="mdi-drag-vertical"
      style="cursor: grabbing"
    />
    <div class="tree-node-content">
      <icon-loader :name="props.item.elementType" />
      <span>{{ props.item.title }}</span>
    </div>
  </div> -->
</template>
<script setup>
import {
  computed,
  inject,
  onMounted,
  ref,
  useTemplateRef,
  onUnmounted,
  watch
} from "vue";
import IconLoader from "../tools/IconLoader.vue";
// import { useDraggable } from "@vueuse/core";
import { useMainStore } from "@/stores/mainStore";
import { storeToRefs } from "pinia";
import { useSpaceStore } from "../../stores/spaceStore";
import { useCalculationStore } from "../../stores/calculationStore";

import elementInputs from "@/data/generated/inputLocation.json";
import { useTreeStore } from "@/stores/treeStore";

const mainStore = useMainStore();
const spaceStore = useSpaceStore();
const calcationStoreNew = useCalculationStore();
const treeStore = useTreeStore();
const { dragItem } = storeToRefs(mainStore);

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  level: {
    type: Number,
    default: 0
  }
});

const openList = inject("tree-open");
const activeTree = inject("tree-active");
const visible = inject("tree-visible");
const handleOpen = inject("handle-open");
const handleActive = inject("handle-active");

const isDragging = ref(false);

const dragHandle = useTemplateRef("treeNodeDrag");

const droppingArea = useTemplateRef("dropArea");

const dropInArea = ref(false);

const dragEnterCounter = ref(0);

const isValidDropArea = computed(() => {
  if (dragItem.value === null) {
    return false;
  }

  if (props.item.id.startsWith("copy-")) {
    return false;
  }

  // do not drop into itself
  if (
    mainStore.dragItem.id
      .replace("_manufacturingStep", "_part")
      .replace("_variant", "_part") ===
    props.item.id
      .replace("_manufacturingStep", "_part")
      .replace("_variant", "_part")
  ) {
    return false;
  }

  // all children are forbidden
  if (
    treeStore.elementBreadcrumbs[props.item.id]
      .map(
        (element) =>
          treeStore.breadcrumpElements[element].id +
          "_" +
          treeStore.breadcrumpElements[element].type
      )
      .includes(dragItem.value.id)
  ) {
    return false;
  }

  // do not drop in the same place
  if (props.item.children.map((item) => item.id).includes(dragItem.value.id)) {
    return false;
  }

  //is allowed child
  if (!dragItem.value.allowedParentTypes.includes(props.item.elementType)) {
    return false;
  }

  return true;
});

watch(dragEnterCounter, (count) => {
  // console.log(count, isValidDropArea.value);
  if (count === 0) {
    dropInArea.value = false;
  } else if (isValidDropArea.value) {
    dropInArea.value = true;
  } else {
    dropInArea.value = false;
  }
});

const dragEnterCallback = (event) => {
  event.preventDefault();
  dragEnterCounter.value++;
};

const dragLeaveCallback = (event) => {
  event.preventDefault();
  dragEnterCounter.value--;
};

const dropCallback = (event) => {
  // console.log(
  //   "dropCallback",
  //   event.dataTransfer.getData("elementId"),
  //   props.item.id
  // );
  spaceStore.moveElement(
    event.dataTransfer.getData("elementId"),
    props.item.id
  );
  event.preventDefault();
};

const dragOverCallback = (event) => {
  event.preventDefault();
};

watch(dragItem, (dragItem) => {
  if (dragItem === null) {
    dragEnterCounter.value = 0;
    droppingArea.value.removeEventListener("dragenter", dragEnterCallback);

    droppingArea.value.removeEventListener("dragleave", dragLeaveCallback);
    droppingArea.value.removeEventListener("dragover", dragOverCallback);

    droppingArea.value.removeEventListener("drop", dropCallback);
  } else if (dragItem.allowedParentTypes.includes(props.item.elementType)) {
    droppingArea.value.addEventListener("dragenter", dragEnterCallback);

    droppingArea.value.addEventListener("dragover", dragOverCallback);

    droppingArea.value.addEventListener("dragleave", dragLeaveCallback);

    droppingArea.value.addEventListener("drop", dropCallback);
  }
});

const allowedParentTypes = {
  material: ["manufacturingStep"],
  part: ["manufacturingStep", "folder"],
  labour: ["manufacturingStep"],
  machine: ["manufacturingStep"],
  setupStep: ["machine"],
  tool: ["setupStep"],
  fixture: ["setupStep"]
};

onMounted(() => {
  // dragHandle.value.addEventListener("drag", (event) => {
  //   console.log("Drag, event", event.x, event.clientX);
  //   // mainStore.dragItem = { item: props.item };
  // });

  dragHandle.value.addEventListener("dragstart", (event) => {
    console.log("DragStart, event", event);
    const { ...item } = props.item;
    item.id = "copy-" + props.item.id;
    mainStore.dragItem = {
      item,
      id: props.item.id,
      allowedParentTypes: allowedParentTypes[item.elementType]
    };
    isDragging.value = true;
    event.dataTransfer.setData("elementId", props.item.id);
  });

  dragHandle.value.addEventListener("dragend", (event) => {
    // console.log("DragEnd, event", event);
    isDragging.value = false;
    mainStore.dragItem = null;
  });
});

const open = computed(() => {
  return (
    (props.item.id && openList.includes(props.item.id)) || dropInArea.value
  );
});

const active = computed(() => {
  return activeTree.value === props.item.id;
});

const error = computed(() => {
  return spaceStore.fieldStatus.element[props.item.id] === "error";
});

const calculating = computed(() => {
  if (calcationStoreNew.freezeCalculation) {
    const id = props.item.id
      .replace("_variant", "_part")
      .replace("_manufacturingStep", "_part");
    return (
      Object.keys(calcationStoreNew.focusCalculation.normalElements).includes(
        id
      ) ||
      Object.keys(calcationStoreNew.focusCalculation.extraElements).includes(
        id
      ) ||
      calcationStoreNew.focusCalculation.outputQuantityQueue
        .map((element) => element.split("|")[0])
        .includes(id)
    );
  } else if (calcationStoreNew.currentCalculation) {
    return calcationStoreNew.currentCalculation.elements.hasOwnProperty(
      props.item.id
        .replace("_variant", "_part")
        .replace("_manufacturingStep", "_part")
    );
  } else {
    return false;
  }
});

onMounted(() => {
  visible.push(props.item.id);
});

onUnmounted(() => {
  visible.splice(visible.indexOf(props.item.id), 1);
});

watch(active, (isActive) => {
  // TODO.gelhaar implement later
  //   if (isActive) {
  //     document.getElementById(props.item.id).scrollIntoView({
  //       behavior: "auto",
  //       block: "start",
  //       inline: "start"
  //     });
  //   }
});

const chevron = computed(() => {
  if (props.item.children.length || dropInArea.value) {
    return open.value ? "mdi-chevron-down" : "mdi-chevron-right";
  } else {
    return "";
  }
});
</script>
<style>
.tree-node {
  /* width: auto; */
  display: grid;
  grid-template-columns: 20px 30px auto;
  white-space: nowrap;
  padding: 5px 0;
  align-items: center;
}

.tree-node-content {
  width: auto;
  display: grid;
  grid-template-columns: 40px auto 40px;
  align-items: center;
  min-height: 30px;
}

.tree-node.active {
  background-color: rgb(234, 234, 234);
}

.tree-node.error {
  color: red;
}

.tree-node.calculating {
  color: green;
}

.tree-node.allowed-drop {
  color: green;
}

.tree-node.disallowed-drop {
  opacity: 0.5;
}
</style>
